import { useState } from "react";
import { FileUp, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { fmtEur2 } from "@/lib/sales";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const price = (cost, markup) => Math.round(cost * (1 + (Number(markup) || 0) / 100) * 100) / 100;

const Preview = ({ res, sel, setSel, markup }) => (
  <div className="max-h-72 overflow-y-auto rounded-lg border border-slate-100" data-testid="supplier-pdf-preview">
    <table className="w-full text-xs"><thead className="sticky top-0 bg-slate-50 text-left text-slate-500"><tr>
      <th className="w-8 p-2" /><th className="p-2 font-medium">Descrizione</th><th className="p-2 font-medium">Q.tà</th><th className="p-2 text-right font-medium">Costo un.</th><th className="p-2 text-right font-medium">Prezzo cliente</th></tr></thead>
      <tbody>{res.lines.map((l, i) => (
        <tr key={i} className="border-t border-slate-100" data-testid={`supplier-pdf-line-${i}`}>
          <td className="p-2"><Checkbox checked={sel[i]} onCheckedChange={(v) => setSel(sel.map((s, j) => (j === i ? !!v : s)))} data-testid={`supplier-pdf-line-check-${i}`} /></td>
          <td className="p-2">{l.code && <span className="mr-1 font-mono text-slate-400">{l.code}</span>}{l.description}</td>
          <td className="p-2">{l.quantity} {l.unit}</td><td className="p-2 text-right">{fmtEur2(l.unit_cost)}</td>
          <td className="p-2 text-right font-semibold text-ink">{fmtEur2(price(l.unit_cost, markup))}</td></tr>))}</tbody></table>
  </div>
);

export const SupplierPdfDialog = ({ open, onClose, quoteId, onAdd }) => {
  const [file, setFile] = useState(null);
  const [markup, setMarkup] = useState(30);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const [sel, setSel] = useState([]);
  const reset = () => { setFile(null); setRes(null); setSel([]); onClose(); };
  const read = async () => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = (await api.post(`/sales/quotes/${quoteId}/supplier-pdf`, fd, { timeout: 180000 })).data;
      setRes(r); setSel(r.lines.map(() => true));
      toast.success(`PDF archiviato come documento interno ${r.document.number}`);
      if (!r.lines.length) { toast.warning("Nessuna riga prodotto riconosciuta nel PDF."); }
    } catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  const add = () => {
    onAdd(res.lines.filter((_, i) => sel[i]).map((l) => ({ product_id: null, description: l.code ? `${l.code} - ${l.description}` : l.description, quantity: l.quantity, unit: l.unit,
      unit_price: price(l.unit_cost, markup), internal_cost: l.unit_cost, discount_pct: 0, vat_rate: 22, recurrence: "una_tantum" })));
    reset();
  };
  const n = sel.filter(Boolean).length;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && reset()}>
      <DialogContent className="sm:max-w-3xl" data-testid="supplier-pdf-dialog">
        <DialogHeader><DialogTitle>Importa da PDF fornitore</DialogTitle>
          <DialogDescription>Le righe vengono lette in automatico con il costo a noi. Il PDF resta interno, archiviato tra i documenti dell'offerta, e non viene mai inviato al cliente.</DialogDescription></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-[1fr_160px]">
          <div className="space-y-1.5"><Label className="text-xs">PDF del fornitore (max 10 MB)</Label>
            <Input type="file" accept="application/pdf" disabled={busy || !!res} onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="supplier-pdf-file" /></div>
          <div className="space-y-1.5"><Label className="text-xs">Ricarico sul costo %</Label>
            <Input type="number" min="0" step="1" value={markup} onChange={(e) => setMarkup(e.target.value)} data-testid="supplier-pdf-markup" /></div>
        </div>
        {busy && <p className="flex items-center gap-2 text-sm text-slate-500" data-testid="supplier-pdf-loading"><Loader2 className="h-4 w-4 animate-spin" />Lettura del PDF in corso, può richiedere fino a un minuto…</p>}
        {res && res.lines.length > 0 && <Preview res={res} sel={sel} setSel={setSel} markup={markup} />}
        {res && <p className="text-xs text-slate-500">Prezzo cliente = costo + {Number(markup) || 0}%. IVA 22%: puoi modificare tutto riga per riga prima di salvare la bozza.</p>}
        <DialogFooter>
          <Button variant="outline" disabled={busy} onClick={reset} data-testid="supplier-pdf-cancel">Annulla</Button>
          {!res ? <Button disabled={!file || busy} onClick={read} className="gap-1" data-testid="supplier-pdf-read"><FileUp className="h-4 w-4" />Leggi PDF</Button>
            : <Button disabled={!n} onClick={add} data-testid="supplier-pdf-add">Aggiungi {n} righe</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
